import { useEffect, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { AlertTriangle, Copy, X } from "lucide-react";
import { STATUS } from "@/lib/palette";

/**
 * Dialog shell.
 *
 * Rendered through a portal so it escapes any `overflow: hidden` ancestor, and
 * closes on Escape — a dialog you cannot dismiss from the keyboard is a trap
 * for anyone not using a mouse.
 */
export function Modal({
  title,
  subtitle,
  open,
  onClose,
  children,
  footer,
  actions,
  width = "max-w-lg",
}: {
  title: string;
  subtitle?: string;
  open: boolean;
  onClose: () => void;
  children: ReactNode;
  footer?: ReactNode;
  /** Toolbar beside the close button, for actions that operate on the content. */
  actions?: ReactNode;
  width?: string;
}) {
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && onClose();
    document.addEventListener("keydown", onKey);
    // Stop the page behind from scrolling under the dialog.
    const previous = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = previous;
    };
  }, [open, onClose]);

  if (!open) return null;

  return createPortal(
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/60 p-4 backdrop-blur-sm">
      <div
        className={`card my-8 w-full ${width}`}
        role="dialog"
        aria-modal="true"
        aria-label={title}
      >
        <div className="card-header">
          <div className="min-w-0">
            <h2 className="text-sm font-semibold">{title}</h2>
            {subtitle && <p className="mt-0.5 text-xs text-ink-400">{subtitle}</p>}
          </div>
          <div className="flex shrink-0 items-center gap-1">
            {actions}
            <button
              onClick={onClose}
              className="rounded p-1.5 text-ink-400 hover:bg-ink-800 hover:text-ink-200"
              aria-label="Close"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>
        <div className="p-4">{children}</div>
        {footer && (
          <div className="flex items-center justify-end gap-2 border-t border-ink-700/60 px-4 py-3">
            {footer}
          </div>
        )}
      </div>
    </div>,
    document.body,
  );
}

export function Field({
  label,
  hint,
  required,
  children,
}: {
  label: string;
  hint?: string;
  required?: boolean;
  children: ReactNode;
}) {
  return (
    <label className="block space-y-1.5">
      <span className="label">
        {label}
        {required && <span className="ml-1 text-sev-1">*</span>}
      </span>
      {children}
      {hint && <span className="block text-[11px] leading-snug text-ink-500">{hint}</span>}
    </label>
  );
}

export function FormError({ error }: { error: unknown }) {
  if (!error) return null;
  const message = error instanceof Error ? error.message : String(error);
  return (
    <div
      className="rounded-lg border px-3 py-2 text-xs"
      style={{ borderColor: "#d03b3b55", backgroundColor: "#d03b3b1a", color: "#e88f8f" }}
      role="alert"
    >
      {message}
    </div>
  );
}

/**
 * A secret shown exactly once.
 *
 * API keys and device keys are stored as digests, so this is the only moment
 * the plaintext exists. The dialog says so plainly rather than letting someone
 * close it and discover later that it is unrecoverable.
 */
export function SecretReveal({ label, secret }: { label: string; secret: string }) {
  return (
    <div className="space-y-2">
      <div
        className="flex items-start gap-2 rounded-lg border px-3 py-2 text-xs"
        style={{ borderColor: `${STATUS.warning}55`, backgroundColor: `${STATUS.warning}12` }}
      >
        <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" style={{ color: STATUS.warning }} />
        <span className="text-ink-200">
          Copy this now. It is stored only as a hash and cannot be shown again — if you
          lose it, issue a new one and revoke this.
        </span>
      </div>
      <div className="label">{label}</div>
      <div className="flex gap-2">
        <code className="min-w-0 flex-1 overflow-x-auto rounded-lg border border-ink-700 bg-ink-900 px-3 py-2 font-mono text-xs text-ink-100">
          {secret}
        </code>
        <button
          type="button"
          className="btn-ghost shrink-0"
          onClick={() => navigator.clipboard?.writeText(secret)}
          title="Copy to clipboard"
        >
          <Copy className="h-3.5 w-3.5" />
        </button>
      </div>
    </div>
  );
}
